import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Monitor, Wifi, WifiOff, Users, Volume2, VolumeX, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

/**
 * ProgramOutputPage — Clean broadcast window showing the PROGRAM output.
 * No controls, no navigation, no UI chrome.
 * Designed to be shared as a "window" in Zoom/Google Meet.
 *
 * Route: /program-output/:sessionId (sessionId can be event_id or session_id)
 *
 * Supports:
 *   - image, youtube, video, text, livestats, countdown, wait, pdf
 *   - livekit_speakers (LiveKit viewer-only for live camera feeds)
 *   - multi_speakers (grid of multiple speakers)
 *   - audio passthrough for video/youtube content
 */
export default function ProgramOutputPage() {
  const { sessionId } = useParams();
  const [program, setProgram] = useState({ type: "black" });
  const [connected, setConnected] = useState(false);
  const [session, setSession] = useState(null);
  const [event, setEvent] = useState(null);
  const [audioMuted, setAudioMuted] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Camera frame relay (received from Régie via BroadcastChannel)
  const [cameraFrame, setCameraFrame] = useState(null);

  // BroadcastChannel listener for camera frames from Régie
  useEffect(() => {
    if (!sessionId) return;
    const bc = new BroadcastChannel(`glotech-program-camera-${sessionId}`);
    bc.onmessage = (evt) => {
      const msg = evt.data;
      if (msg.type === "camera_frame" && msg.dataUrl) {
        setCameraFrame(msg.dataUrl);
      } else if (msg.type === "camera_stop") {
        setCameraFrame(null);
      }
    };
    return () => bc.close();
  }, [sessionId]);

  // Init: get session data
  useEffect(() => {
    if (!sessionId) return;

    const fetchSession = async () => {
      // Try as session ID first
      try {
        const res = await axios.get(`${API}/api/broadcast/sessions/${sessionId}`);
        if (res.data) {
          setSession(res.data);
          if (res.data.current_program) setProgram(res.data.current_program);
          return;
        }
      } catch {}

      // Try as event ID
      try {
        const res = await axios.get(`${API}/api/broadcast/public/by-event/${sessionId}`);
        if (res.data?.session) {
          setSession(res.data.session);
          setEvent(res.data.event);
          if (res.data.session.current_program) setProgram(res.data.session.current_program);
        }
      } catch {}
    };

    fetchSession();
  }, [sessionId]);

  // WebSocket connection (uses the public program endpoint)
  useEffect(() => {
    if (!sessionId) return;

    const connect = () => {
      // Use public program WS endpoint (no auth required)
      const ws = new WebSocket(`${WS_URL}/api/broadcast/ws/program/${sessionId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        const hb = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 15000);
        ws._hb = hb;
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "program_update") {
            setProgram(msg.data || { type: "black" });
          } else if (msg.type === "program_state") {
            setProgram(msg.data || { type: "black" });
          } else if (msg.type === "session_state" && msg.data?.current_program) {
            setProgram(msg.data.current_program);
          }
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(ws._hb);
        setConnected(false);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [sessionId]);

  // LiveKit viewer for speaker feeds
  const [lkRoom, setLkRoom] = useState(null);
  const [lkTracks, setLkTracks] = useState([]);

  useEffect(() => {
    if (program.type !== "livekit_speakers" && program.type !== "multi_speakers") {
      // Disconnect LiveKit when not needed
      if (lkRoom) {
        lkRoom.disconnect();
        setLkRoom(null);
        setLkTracks([]);
      }
      return;
    }

    const roomName = program.config?.room_name;
    const lkUrl = program.config?.livekit_url;
    const viewerToken = program.config?.viewer_token;

    if (!roomName || !viewerToken || !lkUrl) return;

    const connectLK = async () => {
      try {
        const { Room, RoomEvent } = await import("livekit-client");
        const room = new Room();

        room.on(RoomEvent.TrackSubscribed, () => updateTracks(room));
        room.on(RoomEvent.TrackUnsubscribed, () => updateTracks(room));
        room.on(RoomEvent.ParticipantConnected, () => updateTracks(room));
        room.on(RoomEvent.ParticipantDisconnected, () => updateTracks(room));

        await room.connect(lkUrl, viewerToken);
        setLkRoom(room);
        updateTracks(room);
      } catch (e) {
        console.error("LiveKit viewer connect error:", e);
      }
    };

    const updateTracks = (room) => {
      const tracks = [];
      for (const p of room.remoteParticipants.values()) {
        const videoTrack = p.getTrackPublications().find(t => t.kind === "video" && t.isSubscribed);
        const audioTrack = p.getTrackPublications().find(t => t.kind === "audio" && t.isSubscribed);
        if (videoTrack) {
          tracks.push({
            participantId: p.identity,
            participantName: p.name || p.identity,
            videoTrack: videoTrack.track,
            audioTrack: audioTrack?.track,
          });
        }
      }
      setLkTracks([...tracks]);
    };

    connectLK();
    return () => {
      if (lkRoom) lkRoom.disconnect();
    };
  }, [program.type, program.config?.room_name, program.config?.viewer_token]);

  const renderContent = () => {
    // BLACK
    if (program.type === "black" || !program.type) {
      return <div className="w-full h-full bg-black" />;
    }

    // IMAGE
    if (program.type === "image" && program.config?.url) {
      return <img src={program.config.url} alt="Program" className="w-full h-full object-contain bg-black" />;
    }

    // YOUTUBE
    if (program.type === "youtube" && program.config?.video_id) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${program.config.video_id}?autoplay=1&controls=0&modestbranding=1&rel=0`}
          className="w-full h-full" frameBorder="0"
          allow="autoplay; encrypted-media; fullscreen" allowFullScreen
        />
      );
    }

    // VIDEO (local or url)
    if ((program.type === "video_url" || program.type === "local_video") && program.config?.url) {
      return (
        <video
          src={program.config.url}
          autoPlay
          loop={program.config?.loop !== false}
          muted={audioMuted}
          className="w-full h-full object-contain bg-black"
        />
      );
    }

    // TEXT
    if (program.type === "text" && program.config?.text) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-black p-8">
          <p className="text-white text-center" style={{
            fontSize: `${program.config.font_size || 48}px`,
            fontFamily: program.config.font || "system-ui",
            color: program.config.color || "#ffffff",
          }}>
            {program.config.text}
          </p>
        </div>
      );
    }

    // LIVE STATS
    if (program.type === "livestats") {
      return <LiveStatsDisplay config={program.config} sessionId={session?.id} broadcastKey={session?.broadcast_key} />;
    }

    // COUNTDOWN
    if (program.type === "countdown" && program.config) {
      return <CountdownDisplay config={program.config} />;
    }

    // WAIT
    if (program.type === "wait") {
      return (
        <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
          <div className="text-center space-y-4">
            {program.config?.logo_url && (
              <img src={program.config.logo_url} alt="" className="h-16 mx-auto mb-4" />
            )}
            <div className="text-2xl text-[#94A3B8]">{program.config?.message || "L'evenement va bientot commencer..."}</div>
            <div className="flex justify-center gap-1">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2 h-2 rounded-full bg-[#0099CC] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // PDF
    if (program.type === "pdf") {
      const url = program.config?.full_url || program.config?.pages?.[program.config?.page_num - 1]?.full_url;
      if (url) return <img src={url} alt={`PDF Page`} className="w-full h-full object-contain bg-black" />;
    }

    // LIVEKIT SPEAKERS (single or multi)
    if (program.type === "livekit_speakers" || program.type === "multi_speakers") {
      return <LiveKitSpeakersDisplay tracks={lkTracks} config={program.config} audioMuted={audioMuted} />;
    }

    // CAMERA (frames relayed from Régie via BroadcastChannel)
    if (program.type === "camera") {
      if (cameraFrame) {
        return <img src={cameraFrame} alt="Camera" className="w-full h-full object-contain bg-black" />;
      }
      return (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#22D3EE]/30 border-t-[#22D3EE] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">En attente du flux camera...</p>
            <p className="text-[#4A5568] text-xs mt-1">La Regie doit etre ouverte pour diffuser la camera</p>
          </div>
        </div>
      );
    }

    // SCENE COMPOSITION (multi-source split displayed via camera frame relay from Régie)
    if (program.type === "scene") {
      if (cameraFrame) {
        return <img src={cameraFrame} alt="Scene" className="w-full h-full object-contain bg-black" />;
      }
      return (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#EC4899]/30 border-t-[#EC4899] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">Scene en cours de diffusion...</p>
          </div>
        </div>
      );
    }

    // IP CAMERA (HLS/direct stream)
    if (program.type === "ip_camera" && program.config?.url) {
      return <IpCameraOutput url={program.config.url} format={program.config.format} audioMuted={audioMuted} />;
    }

    return <div className="w-full h-full bg-black" />;
  };

  return (
    <div className="w-screen h-screen bg-black overflow-hidden relative select-none" data-testid="program-output-page">
      {renderContent()}

      {/* Tiny overlay controls (hover to reveal) */}
      <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 hover:opacity-100 transition-opacity duration-300">
        {connected ? (
          <Wifi className="h-3 w-3 text-[#10B981]" />
        ) : (
          <WifiOff className="h-3 w-3 text-red-400 animate-pulse" />
        )}
        {(program.type === "video_url" || program.type === "local_video" || program.type === "livekit_speakers" || program.type === "multi_speakers" || program.type === "camera" || program.type === "ip_camera") && (
          <button onClick={() => setAudioMuted(m => !m)} className="p-1 rounded hover:bg-white/10">
            {audioMuted
              ? <VolumeX className="h-3 w-3 text-red-400" />
              : <Volume2 className="h-3 w-3 text-[#10B981]" />
            }
          </button>
        )}
      </div>
    </div>
  );
}


// ===== LIVEKIT SPEAKERS DISPLAY =====

function LiveKitSpeakersDisplay({ tracks, config, audioMuted }) {
  const speakerFilter = config?.speakers; // array of identities, null = show all
  const displayTracks = speakerFilter
    ? tracks.filter(t => speakerFilter.includes(t.participantId))
    : tracks;

  const gridClass = displayTracks.length <= 1
    ? ""
    : displayTracks.length <= 2
    ? "grid grid-cols-2"
    : displayTracks.length <= 4
    ? "grid grid-cols-2 grid-rows-2"
    : "grid grid-cols-3 grid-rows-2";

  if (displayTracks.length === 0) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
        <div className="text-center">
          <Users className="h-12 w-12 text-[#0099CC] mx-auto mb-3 animate-pulse" />
          <p className="text-[#94A3B8] text-lg">En attente des intervenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full bg-black ${gridClass}`}>
      {displayTracks.map(t => (
        <SpeakerVideoTile key={t.participantId} track={t} audioMuted={audioMuted} showName={displayTracks.length > 1} />
      ))}
    </div>
  );
}

function SpeakerVideoTile({ track, audioMuted, showName }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (track.videoTrack && videoRef.current) {
      track.videoTrack.attach(videoRef.current);
      return () => track.videoTrack.detach(videoRef.current);
    }
  }, [track.videoTrack]);

  useEffect(() => {
    if (track.audioTrack && audioRef.current) {
      track.audioTrack.attach(audioRef.current);
      return () => track.audioTrack.detach(audioRef.current);
    }
  }, [track.audioTrack]);

  return (
    <div className="relative w-full h-full">
      <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
      <audio ref={audioRef} autoPlay muted={audioMuted} />
      {showName && (
        <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded text-white text-xs font-medium">
          {track.participantName}
        </div>
      )}
    </div>
  );
}


// ===== LIVE STATS DISPLAY (with auto-refresh) =====

function LiveStatsDisplay({ config, sessionId, broadcastKey }) {
  const [stats, setStats] = useState(config || {});

  useEffect(() => {
    if (!sessionId || !broadcastKey) return;
    const refresh = async () => {
      try {
        const res = await axios.get(`${API}/api/broadcast/public-stats/${sessionId}?key=${broadcastKey}`);
        setStats(res.data.stats);
      } catch {}
    };
    refresh();
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
  }, [sessionId, broadcastKey]);

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
      <div className="text-center">
        <div className="text-7xl font-bold text-[#0099CC] mb-2">{stats.inscrits || stats.total || "—"}</div>
        <div className="text-xl text-[#94A3B8]">Participants inscrits</div>
        <div className="flex gap-8 mt-6 justify-center">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#10B981]">{stats.presents || stats.checked_in || "—"}</div>
            <div className="text-sm text-[#64748b]">Presents</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#F59E0B]">{stats.attente || stats.absent || "—"}</div>
            <div className="text-sm text-[#64748b]">En attente</div>
          </div>
          {stats.attendance_rate !== undefined && (
            <div className="text-center">
              <div className="text-4xl font-bold text-[#8B5CF6]">{stats.attendance_rate}%</div>
              <div className="text-sm text-[#64748b]">Taux presence</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ===== COUNTDOWN =====

function CountdownDisplay({ config }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!config.target_time) return;
    const target = new Date(config.target_time).getTime();
    const tick = () => setRemaining(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [config.target_time]);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
      <div className="text-center">
        {config.label && <div className="text-xl text-[#94A3B8] mb-4">{config.label}</div>}
        <div className="text-7xl font-mono font-bold text-white tracking-wider">
          {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </div>
        {remaining <= 0 && config.end_message && (
          <div className="text-2xl text-[#10B981] mt-4 animate-pulse">{config.end_message}</div>
        )}
      </div>
    </div>
  );
}


// ===== IP CAMERA OUTPUT (HLS/Direct stream for PROGRAM) =====
function IpCameraOutput({ url, format, audioMuted }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !videoRef.current) return;
    setError(null);
    const video = videoRef.current;
    const detectedFormat = format === "auto" || !format
      ? (url.toLowerCase().includes(".m3u8") ? "hls" : "direct")
      : format;

    if (detectedFormat === "hls") {
      import("hls.js").then(({ default: Hls }) => {
        if (!Hls.isSupported()) {
          video.src = url;
          video.play().catch(() => {});
          return;
        }
        if (hlsRef.current) hlsRef.current.destroy();
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          capLevelToPlayerSize: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          startLevel: -1, // Auto-select best quality for PROGRAM
        });
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) setError(`Erreur HLS: ${data.details}`);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
      }).catch(() => setError("Impossible de charger hls.js"));
    } else {
      video.src = url;
      video.play().catch(() => {});
      video.onerror = () => setError("Flux video inaccessible ou format non supporte");
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      video.src = "";
    };
  }, [url, format]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = audioMuted;
  }, [audioMuted]);

  if (error) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <div className="text-center px-4">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 text-sm">{error}</p>
          <p className="text-[#4A5568] text-xs mt-1">{url}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black">
      <video ref={videoRef} autoPlay playsInline muted={audioMuted} className="w-full h-full object-contain" data-testid="program-ip-camera-video" />
    </div>
  );
}
